import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Eye, Cookie, Lock } from "lucide-react";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Privacy Policy</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information when you visit Soundfield.
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Last updated: December 2024
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {/* Information We Collect */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Information You Provide</h3>
                <p className="text-muted-foreground">
                  When you contact us, subscribe to our newsletter, or interact with our site, you may provide us with personal information such as your name, email address, and any messages you send us.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Automatically Collected Information</h3>
                <p className="text-muted-foreground">
                  We may automatically collect certain information about your visit, including your IP address, browser type, referring website, pages viewed, and the time and date of your visit.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                How We Use Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-muted-foreground">
                <li>• To respond to your inquiries and provide customer support</li>
                <li>• To send you newsletters and updates (only if you've subscribed)</li>
                <li>• To improve our website and user experience</li>
                <li>• To analyze website traffic and usage patterns</li>
                <li>• To comply with legal obligations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Cookies and Tracking */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="h-5 w-5" />
                Cookies and Tracking Technologies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                We may use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small text files stored on your device that help us remember your preferences and improve site functionality.
              </p>
              
              <div>
                <h3 className="font-semibold mb-2">Types of Cookies We Use</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• <strong>Essential cookies:</strong> Required for basic site functionality</li>
                  <li>• <strong>Analytics cookies:</strong> Help us understand how visitors use our site</li>
                  <li>• <strong>Preference cookies:</strong> Remember your settings and preferences</li>
                </ul>
              </div>
              
              <p className="text-muted-foreground">
                You can control cookies through your browser settings, but disabling certain cookies may affect site functionality.
              </p>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Data Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
              
              <p className="text-muted-foreground">
                However, please note that no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee absolute security.
              </p>
            </CardContent>
          </Card>

          {/* Third-Party Services */}
          <Card>
            <CardHeader>
              <CardTitle>Third-Party Services</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Our website may contain links to third-party websites or services. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
              </p>
              
              <p className="text-muted-foreground">
                We may use third-party analytics services to help us understand how our website is used. These services may collect information sent by your browser as part of a web page request.
              </p>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card>
            <CardHeader>
              <CardTitle>Your Privacy Rights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              
              <ul className="space-y-2 text-muted-foreground">
                <li>• The right to access your personal information</li>
                <li>• The right to correct inaccurate information</li>
                <li>• The right to delete your personal information</li>
                <li>• The right to restrict or object to processing</li>
                <li>• The right to data portability</li>
              </ul>
              
              <p className="text-muted-foreground">
                To exercise these rights, please contact us using the information provided in our Contact page.
              </p>
            </CardContent>
          </Card>

          {/* Contact and Changes */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Us & Policy Changes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Questions About This Policy</h3>
                <p className="text-muted-foreground">
                  If you have any questions about this Privacy Policy or our privacy practices, please contact us at{" "}
                  <a href="/contact" className="text-primary hover:underline">
                    our contact page
                  </a>.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Policy Updates</h3>
                <p className="text-muted-foreground">
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 py-12">
        <div className="container">
          <div className="text-center text-sm text-muted-foreground">
            © 2024 Soundfield. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;